import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { ordersAPI } from '../services/api';
import CombinedSkuView from './sku/CombinedSkuView';
import { can } from '../services/session';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://store.comparehomeupgrades.com/api';
const FILTERS = ['Daily', 'Weekly', 'Monthly'];
const PLATFORMS = [
  { id: 'amazon',  label: 'Amazon',  color: '#E78C2E' },
  { id: 'shopify', label: 'Shopify', color: '#3B8E59' },
  { id: 'ebay',    label: 'eBay',    color: '#3A6EC8' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function getDateRange(filter, customStart, customEnd) {
  if (filter === 'Custom') {
    if (!customStart || !customEnd) return null;
    return { start: customStart, end: customEnd + ' 23:59:59' };
  }
  const today = new Date();
  const end   = today.toISOString().split('T')[0];
  let start;
  if (filter === 'Daily') {
    start = end;
  } else if (filter === 'Weekly') {
    const d = new Date(today);
    d.setDate(d.getDate() - 6);   // rolling 7 days
    start = d.toISOString().split('T')[0];
  } else {
    const d = new Date(today);
    d.setDate(d.getDate() - 29);  // rolling 30 days
    start = d.toISOString().split('T')[0];
  }
  return { start, end: end + ' 23:59:59' };
}

function getFilterLabel(filter, customStart, customEnd) {
  if (filter === 'Daily') return 'today';
  if (filter === 'Weekly') return 'this week';
  if (filter === 'Monthly') return 'this month';
  if (customStart && customEnd) return `${customStart} → ${customEnd}`;
  return 'select a date range';
}

// ── Shared store cards UI (Amazon + eBay) ─────────────────────────────────────

function fmtMoney(v) {
  return `£${(parseFloat(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function StoreCards({ platform, stores, stats, syncing, loading, total, totalRevenue, revenueKey, filterLabel, onSync }) {
  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
          style={{ backgroundColor: platform.color }} />
        <span className="text-white font-semibold text-base">{platform.label}</span>
        <span className="text-gray-400 text-sm">
          {stores.length} {stores.length === 1 ? 'store' : 'stores'} ·{' '}
          {loading ? '…' : total.toLocaleString()} orders · {loading ? '…' : fmtMoney(totalRevenue)} {filterLabel}
        </span>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm py-3 text-center">Loading…</div>
      ) : stores.length === 0 ? (
        <div className="text-gray-500 text-sm py-3 text-center">No stores configured</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {stores.map((store) => {
            const st        = stats[store.id];
            const orders    = st?.total_orders || 0;
            const revenue   = st?.[revenueKey] || 0;
            const isSyncing = !!syncing[store.id];
            return (
              <div key={store.id}
                className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 hover:bg-white/10 transition-colors">
                <div className="flex items-start justify-between gap-1 mb-1">
                  <p className="text-xs text-gray-400 truncate flex-1" title={store.store_name}>
                    {store.store_name}
                  </p>
                  <button
                    onClick={() => onSync(store.id)}
                    disabled={isSyncing}
                    title="Sync orders"
                    className="text-gray-500 hover:text-white transition-colors disabled:opacity-40 flex-shrink-0 mt-0.5"
                  >
                    <RefreshCw size={11} className={isSyncing ? 'animate-spin' : ''} />
                  </button>
                </div>
                <p className="text-2xl font-semibold text-white leading-tight">
                  {orders.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">orders</p>
                <p className="text-sm font-medium text-gray-300 mt-1">{fmtMoney(revenue)}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Amazon ────────────────────────────────────────────────────────────────────

const AMAZON_CHANNELS = [
  { label: 'All',  value: '' },
  { label: 'FBM',  value: 'MFN' },
  { label: 'FBA',  value: 'AFN' },
];

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

const AmazonSection = forwardRef(function AmazonSection({ filter, customStart, customEnd, onTotals }, ref) {
  const [stores, setStores]     = useState([]);
  const [stats, setStats]       = useState({});
  const [syncing, setSyncing]   = useState({});
  const [loading, setLoading]   = useState(true);
  const [channel, setChannel]   = useState(''); // '' | 'MFN' | 'AFN'

  const fetchAllStats = useCallback(async (list, f, ch, cs, ce) => {
    const range = getDateRange(f, cs, ce);
    if (!range) { setStats({}); return; }
    const { start, end } = range;
    const results = await Promise.all(
      list.map(async (store) => {
        try {
          const params = new URLSearchParams({ start_date: start, end_date: end });
          if (ch) params.append('fulfillment_channel', ch);
          const res  = await fetch(`${API_BASE_URL}/amazon/${store.id}/orders/stats?${params}`, { headers: getAuthHeaders() });
          const data = await res.json();
          return [store.id, data.success ? data.stats : null];
        } catch { return [store.id, null]; }
      })
    );
    setStats(Object.fromEntries(results));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res  = await fetch(`${API_BASE_URL}/amazon/stores`, { headers: getAuthHeaders() });
        const data = await res.json();
        const list = data.stores || [];
        setStores(list);
        await fetchAllStats(list, filter, channel, customStart, customEnd);
      } catch (e) { console.error('Amazon stores error', e); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (stores.length) fetchAllStats(stores, filter, channel, customStart, customEnd);
  }, [filter, channel, customStart, customEnd]);

  // Uses the background sync route and polls it: the synchronous route gets killed by the
  // host's request timeout on anything but a tiny sync. force_full:false = recent orders
  // only (the route defaults to a full 2020-to-now history sync when it isn't set).
  const runSync = async (storeId) => {
    setSyncing(s => ({ ...s, [storeId]: true }));
    try {
      const base = `${API_BASE_URL}/amazon/${storeId}/orders/sync-background`;
      const startRes = await fetch(base, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ force_full: false })
      });
      const start = await startRes.json();
      if (!start.success) return { ok: false, error: start.error || 'could not start' };
      if (start.message === 'Sync already running') return { ok: true, alreadyRunning: true };

      const deadline = Date.now() + 20 * 60 * 1000;
      while (Date.now() < deadline) {
        await sleep(4000);
        const st = (await (await fetch(`${base}/status`, { headers: getAuthHeaders() })).json()).status;
        if (st?.phase === 'done')  return { ok: true, synced: st.synced };
        if (st?.phase === 'error') return { ok: false, error: st.error };
        if (!st || st.phase === 'idle') return { ok: false, error: 'interrupted by a server restart — try again' };
      }
      return { ok: true, stillRunning: true };
    } catch (e) {
      return { ok: false, error: e.message };
    } finally {
      setSyncing(s => ({ ...s, [storeId]: false }));
    }
  };

  const handleSync = async (storeId) => {
    const r = await runSync(storeId);
    if (r.ok && r.alreadyRunning) toast.info('Amazon: a sync is already running for this store');
    else if (r.ok && r.stillRunning) toast.info('Amazon: still syncing in the background — refresh stats shortly');
    else if (r.ok) toast.success(`Amazon: Synced ${r.synced} order items!`);
    else toast.error(`Amazon sync failed: ${r.error}`);
    fetchAllStats(stores, filter, channel, customStart, customEnd);
  };

  useImperativeHandle(ref, () => ({
    syncAll: async () => {
      const results = await Promise.all(stores.map(s => runSync(s.id)));
      fetchAllStats(stores, filter, channel, customStart, customEnd);
      return { label: 'Amazon', results };
    },
  }), [stores, filter, channel, customStart, customEnd, fetchAllStats]);

  const total        = Object.values(stats).reduce((s, st) => s + (st?.total_orders || 0), 0);
  const totalRevenue = Object.values(stats).reduce((s, st) => s + (parseFloat(st?.total_revenue) || 0), 0);
  const filterLabel = getFilterLabel(filter, customStart, customEnd);
  useEffect(() => { onTotals?.('amazon', { orders: total, revenue: totalRevenue, stores: stores.length, loading, note: channel ? (channel === 'MFN' ? 'FBM only' : 'FBA only') : '' }); },
    [total, totalRevenue, stores.length, loading, channel, onTotals]);

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
            style={{ backgroundColor: PLATFORMS[0].color }} />
          <span className="text-white font-semibold text-base">Amazon</span>
          <span className="text-gray-400 text-sm">
            {stores.length} {stores.length === 1 ? 'store' : 'stores'} ·{` `}
            {loading ? '…' : total.toLocaleString()} orders · {loading ? '…' : fmtMoney(totalRevenue)} {filterLabel}
            {channel ? ` (${channel === 'MFN' ? 'FBM' : 'FBA'})` : ''}
          </span>
        </div>
        {/* FBA / FBM channel tabs */}
        <div className="flex gap-1">
          {AMAZON_CHANNELS.map(c => (
            <button key={c.value} onClick={() => setChannel(c.value)}
              className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-all duration-150 ${
                channel === c.value
                  ? c.value === 'AFN' ? 'bg-blue-600/70 text-white border-blue-500'
                  : c.value === 'MFN' ? 'bg-orange-600/70 text-white border-orange-500'
                  : 'bg-white/15 text-white border-white/30'
                  : 'bg-transparent text-gray-400 border-white/10 hover:border-white/20 hover:text-gray-300'
              }`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm py-3 text-center">Loading…</div>
      ) : stores.length === 0 ? (
        <div className="text-gray-500 text-sm py-3 text-center">No stores configured</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {stores.map((store) => {
            const st        = stats[store.id];
            const orders    = st?.total_orders || 0;
            const revenue   = st?.total_revenue || 0;
            const isSyncing = !!syncing[store.id];
            return (
              <div key={store.id}
                className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 hover:bg-white/10 transition-colors">
                <div className="flex items-start justify-between gap-1 mb-1">
                  <p className="text-xs text-gray-400 truncate flex-1" title={store.store_name}>
                    {store.store_name}
                  </p>
                  <button onClick={() => handleSync(store.id)} disabled={isSyncing}
                    title="Sync orders"
                    className="text-gray-500 hover:text-white transition-colors disabled:opacity-40 flex-shrink-0 mt-0.5">
                    <RefreshCw size={11} className={isSyncing ? 'animate-spin' : ''} />
                  </button>
                </div>
                <p className="text-2xl font-semibold text-white leading-tight">{orders.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-0.5">orders</p>
                <p className="text-sm font-medium text-gray-300 mt-1">{fmtMoney(revenue)}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

// ── eBay ──────────────────────────────────────────────────────────────────────

const EbaySection = forwardRef(function EbaySection({ filter, customStart, customEnd, onTotals }, ref) {
  const [stores, setStores]   = useState([]);
  const [stats, setStats]     = useState({});
  const [syncing, setSyncing] = useState({});
  const [loading, setLoading] = useState(true);

  // ✅ Now correctly passes start_date & end_date — backend supports it
  const fetchAllStats = useCallback(async (list, f, cs, ce) => {
    const range = getDateRange(f, cs, ce);
    if (!range) { setStats({}); return; }
    const { start, end } = range;
    const results = await Promise.all(
      list.map(async (store) => {
        try {
          const params = new URLSearchParams({ start_date: start, end_date: end });
          const res  = await fetch(`${API_BASE_URL}/ebay/${store.id}/orders/stats?${params}`, { headers: getAuthHeaders() });
          const data = await res.json();
          return [store.id, data.success ? data.stats : null];
        } catch { return [store.id, null]; }
      })
    );
    setStats(Object.fromEntries(results));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res  = await fetch(`${API_BASE_URL}/ebay/stores`, { headers: getAuthHeaders() });
        const data = await res.json();
        const list = data.stores || [];
        setStores(list);
        await fetchAllStats(list, filter, customStart, customEnd);
      } catch (e) { console.error('eBay stores error', e); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (stores.length) fetchAllStats(stores, filter, customStart, customEnd);
  }, [filter, customStart, customEnd]);

  const runSync = async (storeId) => {
    setSyncing(s => ({ ...s, [storeId]: true }));
    try {
      const res  = await fetch(`${API_BASE_URL}/ebay/${storeId}/orders/sync`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({})
      });
      const data = await res.json();
      return data.success ? { ok: true, synced: data.synced } : { ok: false, error: data.error || 'sync failed' };
    } catch (e) {
      return { ok: false, error: e.message };
    } finally {
      setSyncing(s => ({ ...s, [storeId]: false }));
    }
  };

  const handleSync = async (storeId) => {
    const r = await runSync(storeId);
    if (r.ok) toast.success(`eBay: Synced ${r.synced} orders!`);
    else toast.error(`eBay sync failed: ${r.error}`);
    fetchAllStats(stores, filter, customStart, customEnd);
  };

  useImperativeHandle(ref, () => ({
    syncAll: async () => {
      const results = await Promise.all(stores.map(s => runSync(s.id)));
      fetchAllStats(stores, filter, customStart, customEnd);
      return { label: 'eBay', results };
    },
  }), [stores, filter, customStart, customEnd, fetchAllStats]);

  const total        = Object.values(stats).reduce((s, st) => s + (st?.total_orders || 0), 0);
  const totalRevenue = Object.values(stats).reduce((s, st) => s + (parseFloat(st?.total_sales) || 0), 0);
  useEffect(() => { onTotals?.('ebay', { orders: total, revenue: totalRevenue, stores: stores.length, loading }); },
    [total, totalRevenue, stores.length, loading, onTotals]);

  return (
    <StoreCards
      platform={PLATFORMS[2]}
      stores={stores} stats={stats} syncing={syncing}
      loading={loading} total={total} totalRevenue={totalRevenue} revenueKey="total_sales"
      filterLabel={getFilterLabel(filter, customStart, customEnd)}
      onSync={handleSync}
    />
  );
});

// ── Shopify ───────────────────────────────────────────────────────────────────

const ShopifySection = forwardRef(function ShopifySection({ filter, customStart, customEnd, onTotals }, ref) {
  const [stats, setStats]     = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async (f, cs, ce) => {
    try {
      setLoading(true);
      const range = getDateRange(f, cs, ce);
      if (!range) { setStats(null); return; }
      const { start, end } = range;
      const res = await ordersAPI.getStats({ start_date: start, end_date: end });
      setStats(res.data.stats);
    } catch (e) { console.error('Shopify stats error', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStats(filter, customStart, customEnd); }, [filter, customStart, customEnd]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const res = await ordersAPI.sync({});
      return { ok: true, synced: res.data.synced };
    } catch (e) {
      return { ok: false, error: e.message };
    } finally {
      setSyncing(false);
    }
  };

  const handleSync = async () => {
    const r = await runSync();
    if (r.ok) toast.success(`Shopify: Synced ${r.synced} orders!`);
    else toast.error('Shopify sync failed');
    fetchStats(filter, customStart, customEnd);
  };

  useImperativeHandle(ref, () => ({
    syncAll: async () => {
      const result = await runSync();
      fetchStats(filter, customStart, customEnd);
      return { label: 'Shopify', results: [result] };
    },
  }), [filter, customStart, customEnd, fetchStats]);

  const orders      = stats?.total_orders || 0;
  const revenue     = stats?.total_sales || 0;
  useEffect(() => { onTotals?.('shopify', { orders, revenue: parseFloat(revenue) || 0, stores: 1, loading }); },
    [orders, revenue, loading, onTotals]);
  const filterLabel = getFilterLabel(filter, customStart, customEnd);

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
          style={{ backgroundColor: PLATFORMS[1].color }} />
        <span className="text-white font-semibold text-base">Shopify</span>
        <span className="text-gray-400 text-sm">
          {loading ? '…' : orders.toLocaleString()} orders · {loading ? '…' : fmtMoney(revenue)} {filterLabel}
        </span>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm py-3 text-center">Loading…</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {/* ✅ Shopify store card — same pattern as Amazon/eBay */}
          <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 hover:bg-white/10 transition-colors">
            <div className="flex items-start justify-between gap-1 mb-1">
              <p className="text-xs text-gray-400 truncate flex-1">Main Store</p>
              <button
                onClick={handleSync}
                disabled={syncing}
                title="Sync orders"
                className="text-gray-500 hover:text-white transition-colors disabled:opacity-40 flex-shrink-0 mt-0.5"
              >
                <RefreshCw size={11} className={syncing ? 'animate-spin' : ''} />
              </button>
            </div>
            <p className="text-2xl font-semibold text-white leading-tight">
              {orders.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">orders</p>
            <p className="text-sm font-medium text-gray-300 mt-1">{fmtMoney(revenue)}</p>
          </div>
        </div>
      )}
    </div>
  );
});

// ── All platforms combined ────────────────────────────────────────────────────

function CombinedCard({ totals, filterLabel }) {
  const parts = [
    { key: 'amazon', label: 'Amazon', color: PLATFORMS[0].color },
    { key: 'shopify', label: 'Shopify', color: PLATFORMS[1].color },
    { key: 'ebay', label: 'eBay', color: PLATFORMS[2].color },
  ].map(p => ({ ...p, ...(totals[p.key] || { orders: 0, revenue: 0, stores: 0, loading: true }) }));
  const loading = parts.some(p => p.loading);
  const orders = parts.reduce((s, p) => s + (p.orders || 0), 0);
  const revenue = parts.reduce((s, p) => s + (p.revenue || 0), 0);
  const stores = parts.reduce((s, p) => s + (p.stores || 0), 0);
  const avg = orders ? revenue / orders : 0;
  const amazonNote = totals.amazon?.note;
  return (
    <div className="mt-6 bg-white/10 border border-white/20 rounded-2xl p-5">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <span className="text-white font-semibold text-base">All platforms &amp; stores</span>
        <span className="text-gray-400 text-sm">{stores} stores · {filterLabel}{amazonNote ? ` · Amazon: ${amazonNote}` : ''}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div>
          <p className="text-xs text-gray-400">Orders</p>
          <p className="text-3xl font-semibold text-white leading-tight">{loading ? '…' : orders.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400">Revenue</p>
          <p className="text-3xl font-semibold text-white leading-tight">{loading ? '…' : fmtMoney(revenue)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400">Average per order</p>
          <p className="text-3xl font-semibold text-white leading-tight">{loading ? '…' : `£${avg.toFixed(2)}`}</p>
        </div>
      </div>
      {!loading && revenue > 0 && (
        <div className="flex h-2 rounded-full overflow-hidden bg-white/5 mb-3" title="Share of revenue by platform">
          {parts.map(p => <div key={p.key} style={{ width: `${(p.revenue / revenue) * 100}%`, backgroundColor: p.color }} />)}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {parts.map(p => (
          <div key={p.key} className="flex items-center justify-between text-sm bg-white/5 rounded-lg px-3 py-2">
            <span className="flex items-center gap-2 text-gray-300">
              <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.color }} />{p.label}
            </span>
            <span className="text-white">
              {p.loading ? '…' : `${(p.orders || 0).toLocaleString()} orders · ${fmtMoney(p.revenue)}`}
              {!p.loading && revenue > 0 && <span className="text-gray-500 ml-1">({Math.round((p.revenue / revenue) * 100)}%)</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function OrdersSummary() {
  const showOrders = can('summary');
  const showSku    = can('sku_mapping');
  const canSync    = can('summary', 'edit') || ['shopify', 'amazon', 'ebay'].some(p => can(p, 'edit'));
  const [view, setView]               = useState(showOrders ? 'orders' : 'sku'); // 'orders' | 'sku'
  const [filter, setFilter]           = useState('Weekly');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]     = useState('');

  const activeStart = filter === 'Custom' ? customStart : '';
  const activeEnd   = filter === 'Custom' ? customEnd   : '';

  const amazonRef  = useRef(null);
  const shopifyRef = useRef(null);
  const ebayRef    = useRef(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [totals, setTotals] = useState({});
  const onTotals = useCallback((platform, t) => setTotals(prev => ({ ...prev, [platform]: t })), []);

  // Runs every store's order sync on every platform at the same time, then reports
  // one combined result instead of a toast per store.
  const handleSyncAll = async () => {
    if (syncingAll) return;
    setSyncingAll(true);
    const refs = [amazonRef, shopifyRef, ebayRef];
    const settled = await Promise.allSettled(refs.map(r => r.current?.syncAll()));
    setSyncingAll(false);

    const failures = [];
    let storesSynced = 0, alreadyRunning = 0;
    settled.forEach((s, i) => {
      const label = ['Amazon', 'Shopify', 'eBay'][i];
      if (s.status !== 'fulfilled' || !s.value) { failures.push(`${label}: could not start`); return; }
      s.value.results.forEach(r => {
        if (!r.ok) failures.push(`${label}: ${r.error}`);
        else if (r.alreadyRunning) alreadyRunning++;
        else storesSynced++;
      });
    });

    const note = alreadyRunning ? ` (${alreadyRunning} already syncing in the background)` : '';
    if (failures.length === 0) toast.success(`Sync all complete — ${storesSynced} stores synced${note}`);
    else {
      toast.warn(`Sync all finished: ${storesSynced} stores synced, ${failures.length} failed${note}`);
      failures.forEach(f => toast.error(f, { autoClose: 8000 }));
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto mt-10 px-4">
      <div className="flex items-center justify-center gap-2 mb-5">
        {[
          ...(showOrders ? [{ id: 'orders', label: 'Orders Overview' }] : []),
          ...(showSku ? [{ id: 'sku', label: 'SKU Mapping' }] : []),
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setView(t.id)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-all ${
              view === t.id
                ? 'bg-white/15 text-white border-white/30'
                : 'bg-transparent text-gray-400 border-white/10 hover:text-gray-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {view === 'sku' && showSku && <CombinedSkuView />}

      {view === 'orders' && showOrders && (
      <>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div className="flex items-center gap-3 flex-1">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-gray-400 text-xs uppercase tracking-widest whitespace-nowrap">
            Orders Overview
          </span>
          <div className="h-px flex-1 bg-white/10" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canSync && <button
            onClick={handleSyncAll}
            disabled={syncingAll}
            title="Sync orders for every store on every platform"
            className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border bg-white/10 text-white border-white/30 hover:bg-white/20 transition-all disabled:opacity-50"
          >
            <RefreshCw size={12} className={syncingAll ? 'animate-spin' : ''} />
            {syncingAll ? 'Syncing…' : 'Sync All'}
          </button>}
          <div className="flex gap-1">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 ${
                  filter === f
                    ? 'bg-white/15 text-white border-white/30'
                    : 'bg-transparent text-gray-400 border-white/10 hover:border-white/20 hover:text-gray-300'
                }`}
              >
                {f}
              </button>
            ))}
            <button
              onClick={() => setFilter('Custom')}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150 ${
                filter === 'Custom'
                  ? 'bg-white/15 text-white border-white/30'
                  : 'bg-transparent text-gray-400 border-white/10 hover:border-white/20 hover:text-gray-300'
              }`}
            >
              Custom
            </button>
          </div>

          {filter === 'Custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                max={customEnd || undefined}
                className="bg-white/10 border border-white/20 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-white/40 [color-scheme:dark]"
              />
              <span className="text-gray-500 text-xs">to</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                min={customStart || undefined}
                className="bg-white/10 border border-white/20 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-white/40 [color-scheme:dark]"
              />
            </div>
          )}
        </div>
      </div>

      <CombinedCard totals={totals} filterLabel={getFilterLabel(filter, activeStart, activeEnd)} />
      <AmazonSection  ref={amazonRef}  filter={filter} customStart={activeStart} customEnd={activeEnd} onTotals={onTotals} />
      <div className="border-t border-white/10 mt-6" />
      <ShopifySection ref={shopifyRef} filter={filter} customStart={activeStart} customEnd={activeEnd} onTotals={onTotals} />
      <div className="border-t border-white/10 mt-6" />
      <EbaySection    ref={ebayRef}    filter={filter} customStart={activeStart} customEnd={activeEnd} onTotals={onTotals} />
      </>
      )}
    </div>
  );
}